import { createContext, useContext, useRef, useState, type ReactNode } from 'react';
import { StepUpAal2Dialog, type StepUpAal2DialogState } from '@/components/auth/StepUpAal2Dialog';
import {
  invokeWithStepUp,
  type StepUpPrompt,
  type InvokeWithStepUpOptions,
} from '@/lib/stepUpAal2';

interface QueuedPrompt {
  reason: string;
  mode?: 'totp' | 'sso';
  resolve: (code: string | null) => void;
}

type ProvidedStepUpInvoke = <TArgs, TResult>(
  invocation: Parameters<typeof invokeWithStepUp<TArgs, TResult>>[0],
  options: Omit<InvokeWithStepUpOptions<TArgs>, 'prompt'>,
) => ReturnType<typeof invokeWithStepUp<TArgs, TResult>>;

interface StepUpContextValue {
  /**
   * Wrap any `supabase.functions.invoke` call with step-up retry. When the
   * backend returns `mfa_recent_required` / `mfa_required` /
   * `mfa_assertion_too_old`, opens the global TOTP dialog, awaits a 6-digit
   * code, runs `mfa.challengeAndVerify`, and retries once.
   *
   * The provider mounts a single shared `StepUpAal2Dialog` at the app root
   * so consumers (e.g. hooks like `useSubscription`) don't have to render
   * their own dialog or wire their own state.
   */
  invokeWithStepUp: ProvidedStepUpInvoke;
}

const StepUpContext = createContext<StepUpContextValue | null>(null);

export function StepUpProvider({ children }: { children: ReactNode }) {
  const [dialogState, setDialogState] = useState<StepUpAal2DialogState | null>(null);
  // Pending step-up prompts that arrived while the dialog was already
  // showing another request. Each is resolved in FIFO order so concurrent
  // guarded invocations never leave a Promise dangling.
  const queueRef = useRef<QueuedPrompt[]>([]);
  // Synchronous in-flight flag — `dialogState` is render-time React state
  // and would not flip until after commit, so two prompt() calls in the
  // same tick would both see `null` and both call showQueued, overwriting
  // each other's resolver. The ref flips synchronously so the second call
  // always queues. Codex round-2 race fix.
  const activeRef = useRef(false);

  function showQueued(entry: QueuedPrompt) {
    activeRef.current = true;
    setDialogState({
      open: true,
      reason: entry.reason,
      mode: entry.mode,
      resolve: (code) => {
        // Settle this prompt then dequeue the next one (if any).
        entry.resolve(code);
        const next = queueRef.current.shift();
        if (next) {
          showQueued(next);
        } else {
          activeRef.current = false;
          setDialogState(null);
        }
      },
    });
  }

  const prompt: StepUpPrompt = (input) =>
    new Promise((resolve) => {
      const entry: QueuedPrompt = {
        reason: input.reason,
        mode: input.mode,
        resolve: (code) => resolve(code === null || code === '' ? null : { code }),
      };
      // If a dialog is already showing (or about to show after a
      // synchronous showQueued earlier in this same tick), queue this.
      // Otherwise show it immediately. activeRef flips synchronously so
      // simultaneous prompt() calls in the same tick can't both win.
      if (activeRef.current) {
        queueRef.current.push(entry);
      } else {
        showQueued(entry);
      }
    });

  const wrappedInvoke: ProvidedStepUpInvoke = async (invocation, options) => {
    return invokeWithStepUp(invocation, { ...options, prompt });
  };

  return (
    <StepUpContext.Provider value={{ invokeWithStepUp: wrappedInvoke }}>
      {children}
      <StepUpAal2Dialog state={dialogState} />
    </StepUpContext.Provider>
  );
}

/**
 * Use the global step-up controller. Throws if called outside a
 * `StepUpProvider` so consumers fail fast in tests rather than silently
 * losing the retry behaviour.
 */
export function useStepUpInvoke(): StepUpContextValue['invokeWithStepUp'] {
  const ctx = useContext(StepUpContext);
  if (!ctx) {
    throw new Error('useStepUpInvoke must be used within a StepUpProvider');
  }
  return ctx.invokeWithStepUp;
}
